import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Pagination, Response, Signature } from '@ui/src';
import { Order, PaymentOrder } from '../../../cash-collection/types';
import { transactionApi } from '../../../transactions/store/services';

import ROUTES from '../../../../routes';

export interface ImportTransactionListState {
  importHistory?: PaymentOrder[];
  pagination?: Pagination;
  importItem?: PaymentOrder;
  orders?: Order[];
  signatures?: Signature[];
  takeCount?: string;
}

const initialState: ImportTransactionListState = {};

export const importTransactionListSlice = createSlice({
  name: 'importTransactionList',
  initialState,
  reducers: {
    setStoreTakeCount: (state, action: PayloadAction<string>) => {
      state.takeCount = action.payload;
    },
    clearImportTransactionListState: (state) => {
      state.importHistory = undefined;
      state.pagination = undefined;
      state.takeCount = undefined;
    },
    clearImportTransactionSidepanelState: (state) => {
      state.importItem = undefined;
      state.orders = undefined;
      state.signatures = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsImportHistory.matchFulfilled,
      (state, action: PayloadAction<Response<PaymentOrder>>) => {
        state.importHistory = action.payload.data.edges?.map((item) => ({
          ...item.node,
          acceptedTransactions: item.node.beneficiariesCount.accepted,
          allTransactions: item.node.beneficiariesCount.all,
          procTransactions: item.node.beneficiariesCount.proc,
          rejectedTransactions: item.node.beneficiariesCount.rejected,
          url: generatePath(ROUTES.IMPORT_ITEM, {
            transactionId: item.node.id,
          }),
        }));

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.rejectImportItem.matchFulfilled,
      (state, action: PayloadAction<{ data: PaymentOrder }>) => {
        state.importHistory = state.importHistory?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.status = action.payload.data.status;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getImportItemSignatures.matchFulfilled,
      (state, action: PayloadAction<Response<Signature>>) => {
        state.signatures = action.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsImportItem.matchFulfilled,
      (state, action: PayloadAction<{ data: PaymentOrder }>) => {
        state.importItem = action.payload.data;
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getTransactionsImportItemOrders.matchFulfilled,
      (state, action: PayloadAction<{ data: { beneficiaries: Order[] } }>) => {
        state.orders = action.payload.data.beneficiaries?.map((item) => ({
          ...item,
          originatorsIntermediaryBankName:
            item?.originatorsIntermediaryBankName || '',
        }));
      },
    );
  },
});

export const {
  clearImportTransactionListState,
  clearImportTransactionSidepanelState,
  setStoreTakeCount,
} = importTransactionListSlice.actions;

export default importTransactionListSlice.reducer;
